import type { ActiveSavedSearchParams, SearchParams } from "@/modules/jobAds/client/api/types"

import _isEqual from "lodash/isEqual"
import _omit from "lodash/omit"

import { useJobAdsSearchParams } from "@/modules/jobAds/client/hooks/useJobAdsSearchParams"
import {
  areSearchParamsEmpty,
  computeSearchParamsFromURLSearchParams,
} from "@/modules/jobAds/client/utils/computeSearchParamsFromURLSearchParams"
import { usePageContext } from "@/modules/jobSearch/components/PageContext/PageContextProvider"

type UseSavedSearchesButtonProps = {
  isSearchEmpty: boolean
  isCurrentSearchDifferentFromSavedSearch: boolean
}

export const useSavedSearchesButton = (): UseSavedSearchesButtonProps => {
  const jobAdsSearchParams = useJobAdsSearchParams()
  const { savedSearch } = usePageContext()
  const userSearchParams = computeSearchParamsFromURLSearchParams(jobAdsSearchParams)

  if (areSearchParamsEmpty(userSearchParams)) {
    return {
      isCurrentSearchDifferentFromSavedSearch: false,
      isSearchEmpty: true,
    }
  }

  const getActiveSavedSearchParams = (searchParams: ActiveSavedSearchParams): ActiveSavedSearchParams => {
    const searchParamsConverted = {
      ...searchParams,
      lat: `${searchParams.lat}`,
      lng: `${searchParams.lng}`,
    }

    const activeSearchParamsProperties = Object.entries(searchParamsConverted).filter(([, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0
      }

      return Boolean(value)
    })

    return Object.fromEntries(activeSearchParamsProperties) as ActiveSavedSearchParams
  }

  const getActiveSavedSearchParamsWithOnlyRequiredValues = (searchParams: SearchParams): ActiveSavedSearchParams =>
    getActiveSavedSearchParams(_omit(searchParams, ["searchEngineVersion", "localizedLocation", "locations"]))

  const isCurrentSearchDifferentFromSavedSearch = savedSearch
    ? !_isEqual(
        getActiveSavedSearchParamsWithOnlyRequiredValues(savedSearch.searchParams),
        getActiveSavedSearchParamsWithOnlyRequiredValues(userSearchParams)
      )
    : true

  return {
    isCurrentSearchDifferentFromSavedSearch,
    isSearchEmpty: areSearchParamsEmpty(userSearchParams),
  }
}
